import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type Board, type Clip } from '@air/api/types';

import { type MarqueeSelectableItem, useMarqueeSelection } from '~/hooks/useMarqueeSelection';
import { useMouseScrolling } from '~/hooks/useMouseScrolling';

/** Hit-test ids flow through one flat list, so each kind gets a prefix to keep board/clip ids collision-proof. */
const BOARD_ITEM_ID_PREFIX = 'board:';
const CLIP_ITEM_ID_PREFIX = 'clip:';

export type MarqueeSelectionModifiers = { holdingToggleModifier: boolean };

export type ItemPickerMarqueeSelection = { boards: Board[]; clips: Clip[] };

type RegisteredBoard = { board: Board; element: HTMLElement };
type RegisteredClip = { clip: Clip; element: HTMLElement };
/** Entity travels with the box: virtualization can unmount a swept row mid-drag, and the registry entry goes with it. */
type BoardGestureEntry = { board: Board; box: MarqueeSelectableItem['box'] };
type ClipGestureEntry = { clip: Clip; box: MarqueeSelectableItem['box'] };

type UseItemPickerMarqueeSelectionParams = {
  /**
   * The picker's content column (search + scroll area). Drag STARTS bind here so a
   * gesture can begin in the chrome/padding above the first row and sweep the whole picker —
   * far easier than landing the press inside the dense grid. Coordinates, hit-testing,
   * background-click clearing, and edge auto-scroll all stay on the container/scroll element.
   * Drag-less clicks anywhere in it (off cards/buttons/text fields) clear the selection.
   */
  marqueeAreaRef: RefObject<HTMLDivElement | null>;
  /**
   * The picker's scrollable element (ItemPickerScrollArea's div). Edge auto-scroll measures and
   * drives this element, and it's the events-element fallback when the content column isn't
   * mounted. Owner-provided like marqueeAreaRef, rather than resolved via a DOM closest() walk.
   */
  scrollAreaRef: RefObject<HTMLDivElement | null>;
  /**
   * The search input's immediate wrapper (icon + input + clear button). Presses there keep their
   * native behavior — no marquee start, no edge auto-scroll, no background-click clearing — and
   * containment covers the decorative chrome (the magnifier icon) that tag-name checks miss.
   */
  searchAreaRef: RefObject<HTMLDivElement | null>;
  enableMarqueeSelection: boolean;
  /**
   * Boards stay registered for press detection while merely browsable, but are only hit-tested
   * (and thus selectable by marquee) when this is true. Same idea for clips below.
   */
  areBoardsSelectable: boolean;
  areClipsSelectable: boolean;
  onMarqueeSelectionStart?: (options: MarqueeSelectionModifiers) => void;
  onMarqueeSelectionChange?: (selection: ItemPickerMarqueeSelection, options: MarqueeSelectionModifiers) => void;
  /** Fired for drag-less, modifier-less clicks on non-interactive marquee-area background — the gutters and chrome around the cards. */
  onBackgroundClick?: () => void;
};

/**
 * Cross-section marquee selection for the ItemPicker: one rubber-band gesture sweeps board cards
 * and asset/file cards alike, including drags spanning the boards and assets sections.
 *
 * Structurally mirrors useAssetGridMarqueeSelection (same useMarqueeSelection + useMouseScrolling
 * primitives) but owns TWO element registries and hit-tests them together. The container is the
 * scroll CONTENT element spanning all sections (the coordinate frame and DragSelection render
 * site); the owner passes the content column (marqueeAreaRef, where drag starts bind) and the
 * scroll element (scrollAreaRef, which edge auto-scroll drives).
 *
 * The asset grid's canStartDragOnCard semantics are baked in as always-on: the picker has no card
 * drag-and-drop, and its content is nearly wall-to-wall cards, so a marquee may start anywhere in
 * the content area. Plain clicks stay clicks — the drag library only starts a marquee past its
 * minimum drag area.
 */
export const useItemPickerMarqueeSelection = ({
  marqueeAreaRef,
  scrollAreaRef,
  searchAreaRef,
  enableMarqueeSelection,
  areBoardsSelectable,
  areClipsSelectable,
  onMarqueeSelectionStart,
  onMarqueeSelectionChange,
  onBackgroundClick,
}: UseItemPickerMarqueeSelectionParams) => {
  const [eventsElement, setEventsElement] = useState<HTMLDivElement | null>(null);
  // State (not just a ref) so the events-element resolution below re-runs when the container
  // mounts/unmounts.
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const containerElementRef = useRef<HTMLDivElement | null>(null);
  // The library only fires onSelectionStart once the drag box exceeds its minimum area, so this stays unset for plain clicks and tiny jitters.
  // No initializer: Biome's noUnnecessaryConditions reads useRef's initializer as the ref's forever-value (rules/biome-warning-reactions.md).
  const didMarqueeRef = useRef<boolean>();
  // Captured on mousedown because the drag library's onSelectionStart callback receives no event to read the modifier from.
  const holdingToggleModifierRef = useRef<boolean>(false);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerElementRef.current = node;
    setContainerElement(node);
  }, []);

  // Ref callbacks attach child-first, so the ancestor marqueeAreaRef/scrollAreaRef refs are not
  // assigned yet when the container's (descendant) ref callback runs on mount — resolve the events
  // element in a layout effect, which runs only after ALL refs attach.
  useIsomorphicLayoutEffect(() => {
    if (!containerElement) {
      setEventsElement(null);
      return;
    }

    setEventsElement(marqueeAreaRef.current ?? scrollAreaRef.current ?? containerElement);
  }, [containerElement, marqueeAreaRef, scrollAreaRef]);

  const isMarqueeSelectionEnabled = enableMarqueeSelection && (areBoardsSelectable || areClipsSelectable);

  // Ref registries of mounted card elements, so the hit-test and press guard read React-registered
  // elements instead of querying the DOM for test markers. Registration carries the full entity
  // because the sections fetch their own data — the picker only ever sees what registered here.
  const boardEntriesRef = useRef<Map<Board['id'], RegisteredBoard>>(new Map());
  const clipEntriesRef = useRef<Map<Clip['id'], RegisteredClip>>(new Map());
  // Container-relative boxes measured during the current drag; virtualization unmounts rows the box
  // already swept over, and their cached boxes keep them selectable for the rest of the gesture.
  const boardGesturesRef = useRef<Map<Board['id'], BoardGestureEntry>>(new Map());
  const clipGesturesRef = useRef<Map<Clip['id'], ClipGestureEntry>>(new Map());

  const registerBoardElement = useCallback((board: Board, element: HTMLElement | null) => {
    if (element) {
      boardEntriesRef.current.set(board.id, { board, element });
    } else {
      boardEntriesRef.current.delete(board.id);
    }
  }, []);

  const registerClipElement = useCallback((clip: Clip, element: HTMLElement | null) => {
    if (element) {
      clipEntriesRef.current.set(clip.id, { clip, element });
    } else {
      clipEntriesRef.current.delete(clip.id);
    }
  }, []);

  const isPressOnCard = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Node)) {
      return false;
    }

    for (const { element } of boardEntriesRef.current.values()) {
      if (element.contains(target)) {
        return true;
      }
    }

    for (const { element } of clipEntriesRef.current.values()) {
      if (element.contains(target)) {
        return true;
      }
    }

    return false;
  }, []);

  // Text fields keep their native press behavior (caret placement, text selection) — the search
  // input lives inside the widened events area, so a marquee must never start from it.
  // Tag-name checks catch inputs anywhere in the area; searchAreaRef containment additionally
  // covers the search row's decorative chrome (the magnifier icon sits OUTSIDE the input node,
  // and a press on it must behave like a press on the input, not on empty background).
  const isPressOnTextField = useCallback(
    (target: EventTarget | null) => {
      if (target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return true;
      }

      return target instanceof Node && !!searchAreaRef.current?.contains(target);
    },
    [searchAreaRef],
  );

  // Drag STARTS are allowed anywhere in the events area (the picker's content column), not just
  // inside the sections container — the grid is dense, and the chrome/padding above the first row
  // is the natural place to begin a sweep-everything gesture.
  const shouldStartSelecting = useCallback(
    (target: EventTarget | null) => !isPressOnTextField(target),
    [isPressOnTextField],
  );

  // useMouseScrolling listens on window, so unlike the marquee (whose listeners bind to the events
  // element) it needs an explicit containment check — without it, presses on the sidebar, nav bar,
  // or footer would arm edge auto-scroll of the picker's list.
  const shouldStartScrolling = useCallback(
    (target: EventTarget | null) =>
      isMarqueeSelectionEnabled &&
      target instanceof Node &&
      !!eventsElement?.contains(target) &&
      !isPressOnTextField(target),
    [eventsElement, isMarqueeSelectionEnabled, isPressOnTextField],
  );

  useMouseScrolling({
    scrollElementRef: scrollAreaRef,
    measureBounds: 'element',
    shouldStartScrolling,
    replayTarget: eventsElement,
    isEnabled: isMarqueeSelectionEnabled,
  });

  const getItems = useCallback(() => {
    const containerRect = containerElementRef.current?.getBoundingClientRect();

    if (!containerRect) {
      return [];
    }

    const measureBox = (element: HTMLElement): MarqueeSelectableItem['box'] => {
      const rect = element.getBoundingClientRect();

      return {
        left: rect.left - containerRect.left,
        top: rect.top - containerRect.top,
        width: rect.width,
        height: rect.height,
      };
    };

    if (areBoardsSelectable) {
      boardEntriesRef.current.forEach(({ board, element }, boardId) => {
        boardGesturesRef.current.set(boardId, { board, box: measureBox(element) });
      });
    }

    if (areClipsSelectable) {
      clipEntriesRef.current.forEach(({ clip, element }, clipId) => {
        clipGesturesRef.current.set(clipId, { clip, box: measureBox(element) });
      });
    }

    const items: MarqueeSelectableItem[] = [];

    boardGesturesRef.current.forEach(({ box }, boardId) => {
      items.push({ id: `${BOARD_ITEM_ID_PREFIX}${boardId}`, box });
    });
    clipGesturesRef.current.forEach(({ box }, clipId) => {
      items.push({ id: `${CLIP_ITEM_ID_PREFIX}${clipId}`, box });
    });

    return items;
  }, [areBoardsSelectable, areClipsSelectable]);

  const onHighlightChange = useCallback(
    (ids: string[]) => {
      const boards: Board[] = [];
      const clips: Clip[] = [];

      ids.forEach((id) => {
        if (id.startsWith(BOARD_ITEM_ID_PREFIX)) {
          const entry = boardGesturesRef.current.get(id.slice(BOARD_ITEM_ID_PREFIX.length));

          if (entry) {
            boards.push(entry.board);
          }

          return;
        }

        const entry = clipGesturesRef.current.get(id.slice(CLIP_ITEM_ID_PREFIX.length));

        if (entry) {
          clips.push(entry.clip);
        }
      });

      onMarqueeSelectionChange?.({ boards, clips }, { holdingToggleModifier: holdingToggleModifierRef.current });
    },
    [onMarqueeSelectionChange],
  );

  const handleSelectionStart = useCallback(() => {
    didMarqueeRef.current = true;
    onMarqueeSelectionStart?.({ holdingToggleModifier: holdingToggleModifierRef.current });
  }, [onMarqueeSelectionStart]);

  const { DragSelection } = useMarqueeSelection({
    containerRef: containerElementRef,
    getItems,
    onHighlightChange,
    isEnabled: isMarqueeSelectionEnabled,
    eventsElement,
    shouldStartSelecting,
    onSelectionStart: handleSelectionStart,
  });

  useEffect(() => {
    if (!eventsElement || !isMarqueeSelectionEnabled) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      didMarqueeRef.current = false;
      holdingToggleModifierRef.current = event.metaKey || event.ctrlKey || event.shiftKey;
      boardGesturesRef.current.clear();
      clipGesturesRef.current.clear();
    };

    eventsElement.addEventListener('mousedown', handleMouseDown);

    return () => {
      eventsElement.removeEventListener('mousedown', handleMouseDown);
    };
  }, [eventsElement, isMarqueeSelectionEnabled]);

  useEffect(() => {
    if (!eventsElement || !isMarqueeSelectionEnabled) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      // Suppress the click that follows a marquee drag release so it doesn't clear the selection
      // the drag just made. stopPropagation also keeps the click from reaching React's root
      // delegate: a small drag starting AND ending inside one card would otherwise fire that
      // card's toggle (or a board's navigation) right after the marquee set the selection.
      if (didMarqueeRef.current) {
        event.stopPropagation();
        return;
      }

      // A modifier press means an additive gesture, so missing a card must not wipe the selection.
      const isAdditiveGesture = event.metaKey || event.ctrlKey || event.shiftKey;

      // Buttons cover the interactive chrome between cards (section collapse toggles, and board
      // cards browsed while not selectable) — clicking those must act, not clear the selection.
      const isOnInteractiveControl = event.target instanceof Element && !!event.target.closest('button');

      if (
        !isAdditiveGesture &&
        !isPressOnCard(event.target) &&
        !isOnInteractiveControl &&
        !isPressOnTextField(event.target)
      ) {
        onBackgroundClick?.();
      }
    };

    eventsElement.addEventListener('click', handleClick);

    return () => {
      eventsElement.removeEventListener('click', handleClick);
    };
  }, [eventsElement, isMarqueeSelectionEnabled, isPressOnCard, isPressOnTextField, onBackgroundClick]);

  return { setContainerRef, isMarqueeSelectionEnabled, DragSelection, registerBoardElement, registerClipElement };
};
